import moment from 'moment';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { LOCATION_ROLES } from '../../../../constants';
import { buildExpressQuery } from '../../../../repository/queries/builders';
import getLastLocations from '../../../../repository/geo/getLastLocations';
import { getBulkQueryData } from '../../../../repository/utils';
import { getRouteSessionsQuery } from '../../../../repository/queries';
import { getProcessedFluidRouteSessionQuery } from '../../../../repository/route-sessions/queries';

// Common

const getUserQuery = (id, queryParams = {}) => {
    const endpoint = '/users';
    const params = {
        where: {
            id,
            deleted_at: { eq: null },
            ...queryParams,
        },
    };
    return buildExpressQuery(endpoint, { params });
};

export const fetchUser = async (userId) => {
    const response = await axios.get(getUserQuery(userId));
    return response.data;
};

export const useFetchUser = (id, enabled = true) => {
    return useQuery({
        queryFn: () => fetchUser(id),
        queryKey: ['user-data', id],
        enabled: !!id && enabled,
    });
};

// Producer Management

const getProducerLocationData = async (processorId) => {
    try {
        const lastLocationsResponse = await getLastLocations();

        // TODO: filter by producer_id?
        const routeSessionsQuery = getBulkQueryData(getRouteSessionsQuery, {
            fields: ['_id', 'BOL', 'status', 'route_id', 'hauling_id', 'created_by', 'schedule_supply_item_id', 'pickup_id', 'created_at'],
            status: 'active,inactive',
            populate: { created_by: ['name'], route_id: ['name'], hauling_id: ['name'], schedule_supply_item_id: ['processor_id'], pickup_id: ['volume', 'amount'] },
            ids: lastLocationsResponse.map((location) => location.route_session_id),
            start: moment().subtract(7, 'days').startOf('day').toISOString(),
            end: moment().endOf('day').toISOString(),
        });

        const producersQuery = fetchUser(processorId);

        const [routeSessions, producers] = await Promise.all([routeSessionsQuery, producersQuery]);

        const formattedRouteSessions = [];
        routeSessions.forEach((routeSession) => {
            const correspondingLocation = lastLocationsResponse.find((location) => location.route_session_id === routeSession.id);
            if (correspondingLocation) {
                formattedRouteSessions.push({
                    BOL: routeSession.BOL || routeSession.lt_number,
                    id: routeSession.id,
                    status: routeSession.status,
                    hauling_name: routeSession?.hauling_id?.name,
                    hauling_id: routeSession?.hauling_id?._id,
                    driver_name: routeSession?.created_by?.name || routeSession?.driver_id?.name,
                    route_name: routeSession?.route_id?.name,
                    processor_name: producers[0].name,
                    role: LOCATION_ROLES.DRIVER,
                    type: 'location',
                    location: correspondingLocation?.location,
                    updated_at: correspondingLocation?.updated_at,
                });
            }
        });
        const formattedProducers = producers.map((item) => ({
            name: item.name,
            id: item.id,
            userStatus: item.status,
            role: LOCATION_ROLES.PROCESSOR,
            type: 'location',
            location: { coordinates: [parseFloat(item.longitude || 0), parseFloat(item.latitude || 0)], type: 'Point' },
            license_number: item.license_number,
            address1: item.address1,
            town: item.town,
            postal_code: item.postal_code,
        }));

        return { producers: formattedProducers, routeSessions: formattedRouteSessions };
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        return {};
    }
};

export const useProducerLocationData = (id) => {
    return useQuery({
        queryFn: () => getProducerLocationData(id),
        queryKey: ['producer-location-data', id],
        enabled: !!id,
    });
};

// Processor/Plant Management

const getProcessorLocationData = async (processorId) => {
    try {
        const lastLocationsResponse = await getLastLocations();

        // TODO: filter by processor_id?
        const routeSessionsQuery = getBulkQueryData(getRouteSessionsQuery, {
            fields: ['_id', 'BOL', 'status', 'route_id', 'hauling_id', 'created_by', 'schedule_supply_item_id', 'pickup_id', 'created_at'],
            status: 'active,inactive',
            populate: { created_by: ['name'], route_id: ['name'], hauling_id: ['name'], schedule_supply_item_id: ['processor_id'], pickup_id: ['volume', 'amount'] },
            ids: lastLocationsResponse.map((location) => location.route_session_id),
            start: moment().subtract(7, 'days').startOf('day').toISOString(),
            end: moment().endOf('day').toISOString(),
        });

        const pfRouteSessionsQuery = getBulkQueryData(getProcessedFluidRouteSessionQuery, {
            fields: ['_id', 'lt_number', 'status', 'route_id', 'route_number', 'hauling_id', 'driver_id', 'destination_processor_id'],
            status: 'active,inactive,in_transit',
            populate: { driver_id: ['name'], route_id: ['name'], hauling_id: ['name'], destination_processor_id: ['name'] },
            ids: lastLocationsResponse.map((location) => location.route_session_id),
            start: moment().subtract(7, 'days').startOf('day').toISOString(),
            end: moment().endOf('day').toISOString(),
        });

        const processorQuery = fetchUser(processorId);

        const [routeSessions, pfRouteSessions, processors] = await Promise.all([routeSessionsQuery, pfRouteSessionsQuery, processorQuery]);

        const formattedRouteSessions = [];
        [...routeSessions, ...pfRouteSessions].forEach((routeSession) => {
            const correspondingLocation = lastLocationsResponse.find((location) => location.route_session_id === routeSession.id);
            if (correspondingLocation) {
                formattedRouteSessions.push({
                    BOL: routeSession.BOL || routeSession.lt_number,
                    id: routeSession.id,
                    status: routeSession.status,
                    hauling_name: routeSession?.hauling_id?.name,
                    hauling_id: routeSession?.hauling_id?._id,
                    driver_name: routeSession?.created_by?.name || routeSession?.driver_id?.name,
                    route_name: routeSession?.route_id?.name,
                    processor_name: processors[0].name,
                    role: LOCATION_ROLES.DRIVER,
                    type: 'location',
                    location: correspondingLocation?.location,
                    updated_at: correspondingLocation?.updated_at,
                });
            }
        });
        const formattedProcessors = processors.map((item) => ({
            name: item.name,
            id: item.id,
            userStatus: item.status,
            role: LOCATION_ROLES.PROCESSOR,
            type: 'location',
            location: { coordinates: [parseFloat(item.longitude || 0), parseFloat(item.latitude || 0)], type: 'Point' },
            license_number: item.license_number,
            address1: item.address1,
            town: item.town,
            postal_code: item.postal_code,
        }));

        return { processors: formattedProcessors, routeSessions: formattedRouteSessions };
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        return {};
    }
};

export const useProcessorLocationData = (id) => {
    return useQuery({
        queryFn: () => getProcessorLocationData(id),
        queryKey: ['processor-location-data', id],
        enabled: !!id,
    });
};

// Hauler Management

export const getHaulerLocationData = async (haulerId) => {
    try {
        const lastLocationsResponse = await getLastLocations();

        const routeSessionsQuery = getBulkQueryData(getRouteSessionsQuery, {
            fields: ['_id', 'BOL', 'status', 'route_id', 'hauling_id', 'created_by', 'schedule_supply_item_id', 'pickup_id', 'created_at'],
            status: 'active,inactive',
            populate: { created_by: ['name'], route_id: ['name'], hauling_id: ['name'], schedule_supply_item_id: ['processor_id'], pickup_id: ['volume', 'amount'] },
            ids: lastLocationsResponse.map((location) => location.route_session_id),
            start: moment().subtract(7, 'days').startOf('day').toISOString(),
            end: moment().endOf('day').toISOString(),
        });

        const haulersQuery = fetchUser(haulerId);

        const [routeSessions, haulers] = await Promise.all([routeSessionsQuery, haulersQuery]);

        const formattedRouteSessions = [];
        routeSessions.forEach((routeSession) => {
            const correspondingLocation = lastLocationsResponse.find((location) => location.route_session_id === routeSession.id);
            if (correspondingLocation) {
                formattedRouteSessions.push({
                    BOL: routeSession.BOL || routeSession.lt_number,
                    id: routeSession.id,
                    status: routeSession.status,
                    hauling_name: routeSession?.hauling_id?.name,
                    hauling_id: routeSession?.hauling_id?._id,
                    driver_name: routeSession?.created_by?.name || routeSession?.driver_id?.name,
                    route_name: routeSession?.route_id?.name,
                    processor_name: haulers[0].name,
                    role: LOCATION_ROLES.HAULER,
                    type: 'location',
                    location: correspondingLocation?.location,
                    updated_at: correspondingLocation?.updated_at,
                });
            }
        });

        const formattedHaulers = haulers.map((item) => ({
            name: item.name,
            id: item._id,
            status: item.status,
            role: LOCATION_ROLES.HAULER,
            type: 'location',
            location: { coordinates: [parseFloat(item.longitude || 0), parseFloat(item.latitude || 0)], type: 'Point' },
            license_number: item.hauler_number,
            address1: item.address1,
            town: item.town,
            postal_code: item.postal_code,
        }));

        return { haulers: formattedHaulers, routeSessions: formattedRouteSessions };
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        return {};
    }
};

export const useHaulerLocationData = (id) => {
    return useQuery({
        queryFn: () => getHaulerLocationData(id),
        queryKey: ['hauler-location-data', id],
        enabled: !!id,
    });
};
